/**
 * Viewpoint math module.
 * Provides the vector, angle, ray, and projection helpers shared by search and
 * scoring so those stages can stay focused on viewpoint decisions rather than math.
 */
import type { ViewpointBounds, ViewpointVector3 } from '../generate-jig.types'

/** Returns the diagonal length of an axis-aligned size vector. */
export function diagonal(size: ViewpointVector3): number {
  return Math.sqrt((size.x * size.x) + (size.y * size.y) + (size.z * size.z))
}

/** Subtracts vector `b` from `a`. */
export function sub(a: ViewpointVector3, b: ViewpointVector3): ViewpointVector3 {
  return {
    x: a.x - b.x,
    y: a.y - b.y,
    z: a.z - b.z,
  }
}

/** Dot product between two vectors. */
export function dot(a: ViewpointVector3, b: ViewpointVector3): number {
  return (a.x * b.x) + (a.y * b.y) + (a.z * b.z)
}

/** Cross product between two vectors. */
export function cross(a: ViewpointVector3, b: ViewpointVector3): ViewpointVector3 {
  return {
    x: (a.y * b.z) - (a.z * b.y),
    y: (a.z * b.x) - (a.x * b.z),
    z: (a.x * b.y) - (a.y * b.x),
  }
}

/** Euclidean vector length. */
export function length(vector: ViewpointVector3): number {
  return Math.sqrt(dot(vector, vector))
}

/** Returns a unit vector, falling back to forward-facing Z for degenerate inputs. */
export function normalize(vector: ViewpointVector3): ViewpointVector3 {
  const vectorLength = length(vector)

  if (vectorLength < 1e-6) {
    return { x: 0, y: 0, z: 1 }
  }

  return {
    x: vector.x / vectorLength,
    y: vector.y / vectorLength,
    z: vector.z / vectorLength,
  }
}

/** Multiplies a vector by a scalar. */
export function scale(vector: ViewpointVector3, multiplier: number): ViewpointVector3 {
  return {
    x: vector.x * multiplier,
    y: vector.y * multiplier,
    z: vector.z * multiplier,
  }
}

/** Adds two vectors. */
export function add(a: ViewpointVector3, b: ViewpointVector3): ViewpointVector3 {
  return {
    x: a.x + b.x,
    y: a.y + b.y,
    z: a.z + b.z,
  }
}

/** Converts a direction vector into yaw/pitch angles used by the search planner. */
export function directionToYawPitch(direction: ViewpointVector3): { yawDeg: number; pitchDeg: number } {
  const normalizedDirection = normalize(direction)

  return {
    yawDeg: Math.atan2(normalizedDirection.x, normalizedDirection.z) * (180 / Math.PI),
    pitchDeg: Math.atan2(
      normalizedDirection.y,
      Math.sqrt((normalizedDirection.x * normalizedDirection.x) + (normalizedDirection.z * normalizedDirection.z))
    ) * (180 / Math.PI),
  }
}

/** Reconstructs a direction vector from planner yaw/pitch angles. */
export function yawPitchToDirection(yawDeg: number, pitchDeg: number): ViewpointVector3 {
  const yawRadians = yawDeg * (Math.PI / 180)
  const pitchRadians = pitchDeg * (Math.PI / 180)
  const cosPitch = Math.cos(pitchRadians)

  return normalize({
    x: Math.sin(yawRadians) * cosPitch,
    y: Math.sin(pitchRadians),
    z: Math.cos(yawRadians) * cosPitch,
  })
}

/** Returns the shortest signed angle delta needed to rotate from `fromDeg` to `toDeg`. */
export function shortestAngleDeg(fromDeg: number, toDeg: number): number {
  let delta = (toDeg - fromDeg) % 360

  if (delta > 180) {
    delta -= 360
  }

  if (delta < -180) {
    delta += 360
  }

  return delta
}

function buildAabbCorners(bounds: ViewpointBounds): ViewpointVector3[] {
  const halfX = bounds.size.x / 2
  const halfY = bounds.size.y / 2
  const halfZ = bounds.size.z / 2
  const corners: ViewpointVector3[] = []

  ;[-halfX, halfX].forEach(function (x) {
    ;[-halfY, halfY].forEach(function (y) {
      ;[-halfZ, halfZ].forEach(function (z) {
        corners.push({
          x: bounds.center.x + x,
          y: bounds.center.y + y,
          z: bounds.center.z + z,
        })
      })
    })
  })

  return corners
}

function buildCameraBasis(forwardDirection: ViewpointVector3): {
  forward: ViewpointVector3
  right: ViewpointVector3
  up: ViewpointVector3
} {
  const forward = normalize(forwardDirection)
  let worldUp: ViewpointVector3 = { x: 0, y: 1, z: 0 }

  if (Math.abs(dot(forward, worldUp)) > 0.9999) {
    worldUp = { x: 0, y: 0, z: 1 }
  }

  const right = normalize(cross(worldUp, forward))
  const up = normalize(cross(forward, right))

  return { forward, right, up }
}

/**
 * Finds the smallest distance that still fits the entire target AABB inside the
 * camera frustum, accounting for aspect ratio and the requested frame fill.
 */
export function computeAabbFramedDistance(
  bounds: ViewpointBounds,
  cameraDirectionFromTarget: ViewpointVector3,
  minDistance: number,
  options?: {
    fovDeg?: number
    aspect?: number
    frameFill?: number
    minDepth?: number
  }
): number {
  const fovDeg = options && typeof options.fovDeg === 'number' ? options.fovDeg : 60
  const aspect = options && typeof options.aspect === 'number' ? options.aspect : (16 / 9)
  const frameFill = options && typeof options.frameFill === 'number' ? options.frameFill : 0.9
  const minDepth = options && typeof options.minDepth === 'number' ? options.minDepth : 1e-3

  const forward = scale(normalize(cameraDirectionFromTarget), -1)
  const basis = buildCameraBasis(forward)
  const tanHalfFov = Math.tan((fovDeg * Math.PI) / 360)
  let requiredDistance = minDistance

  buildAabbCorners(bounds).forEach(function (corner) {
    const local = sub(corner, bounds.center)
    const forwardOffset = dot(local, basis.forward)
    const rightOffset = Math.abs(dot(local, basis.right))
    const upOffset = Math.abs(dot(local, basis.up))
    const requiredDepth = Math.max(
      rightOffset / (tanHalfFov * aspect * frameFill),
      upOffset / (tanHalfFov * frameFill),
      minDepth
    )

    requiredDistance = Math.max(requiredDistance, requiredDepth - forwardOffset)
  })

  return Math.max(requiredDistance, minDistance)
}

/**
 * Slab-test intersection used by the coarse occlusion scorer.
 * The caller supplies a finite `rayLength` so hits beyond the target do not count
 * as blockers.
 */
export function rayIntersectsAabb(
  rayOrigin: ViewpointVector3,
  rayDirection: ViewpointVector3,
  boxCenter: ViewpointVector3,
  boxHalfExtents: ViewpointVector3,
  rayLength: number
): boolean {
  const minX = boxCenter.x - boxHalfExtents.x
  const maxX = boxCenter.x + boxHalfExtents.x
  const minY = boxCenter.y - boxHalfExtents.y
  const maxY = boxCenter.y + boxHalfExtents.y
  const minZ = boxCenter.z - boxHalfExtents.z
  const maxZ = boxCenter.z + boxHalfExtents.z

  let tMin = 0
  let tMax = rayLength

  if (Math.abs(rayDirection.x) < 1e-10) {
    if (rayOrigin.x < minX || rayOrigin.x > maxX) {
      return false
    }
  } else {
    const inverse = 1 / rayDirection.x
    let t0 = (minX - rayOrigin.x) * inverse
    let t1 = (maxX - rayOrigin.x) * inverse
    if (t0 > t1) {
      const temp = t0
      t0 = t1
      t1 = temp
    }
    tMin = Math.max(tMin, t0)
    tMax = Math.min(tMax, t1)
    if (tMin > tMax) {
      return false
    }
  }

  if (Math.abs(rayDirection.y) < 1e-10) {
    if (rayOrigin.y < minY || rayOrigin.y > maxY) {
      return false
    }
  } else {
    const inverse = 1 / rayDirection.y
    let t0 = (minY - rayOrigin.y) * inverse
    let t1 = (maxY - rayOrigin.y) * inverse
    if (t0 > t1) {
      const temp = t0
      t0 = t1
      t1 = temp
    }
    tMin = Math.max(tMin, t0)
    tMax = Math.min(tMax, t1)
    if (tMin > tMax) {
      return false
    }
  }

  if (Math.abs(rayDirection.z) < 1e-10) {
    if (rayOrigin.z < minZ || rayOrigin.z > maxZ) {
      return false
    }
  } else {
    const inverse = 1 / rayDirection.z
    let t0 = (minZ - rayOrigin.z) * inverse
    let t1 = (maxZ - rayOrigin.z) * inverse
    if (t0 > t1) {
      const temp = t0
      t0 = t1
      t1 = temp
    }
    tMin = Math.max(tMin, t0)
    tMax = Math.min(tMax, t1)
    if (tMin > tMax) {
      return false
    }
  }

  return true
}

/**
 * Projects an AABB into normalized viewport space so scoring can reason about
 * target coverage and 2D overlap without building a full render pipeline.
 */
export function projectAabbToViewport(
  bounds: ViewpointBounds,
  cameraPosition: ViewpointVector3,
  forwardDirection: ViewpointVector3,
  fovDeg: number,
  aspect: number
): {
  minX: number
  maxX: number
  minY: number
  maxY: number
  width: number
  height: number
  area: number
  minDepth: number
  maxDepth: number
} | null {
  const basis = buildCameraBasis(forwardDirection)
  const tanHalfFov = Math.tan((fovDeg * Math.PI) / 360)
  const projected: Array<{ x: number; y: number; depth: number }> = []

  buildAabbCorners(bounds).forEach(function (corner) {
    const relative = sub(corner, cameraPosition)
    const depth = dot(relative, basis.forward)
    if (depth <= 1e-4) {
      return
    }

    const cameraX = dot(relative, basis.right)
    const cameraY = dot(relative, basis.up)
    const ndcX = cameraX / (depth * tanHalfFov * aspect)
    const ndcY = cameraY / (depth * tanHalfFov)

    projected.push({
      x: Math.max(0, Math.min(1, (ndcX + 1) / 2)),
      y: Math.max(0, Math.min(1, (ndcY + 1) / 2)),
      depth: depth,
    })
  })

  if (!projected.length) {
    return null
  }

  const minX = Math.min.apply(null, projected.map(function (point) { return point.x }))
  const maxX = Math.max.apply(null, projected.map(function (point) { return point.x }))
  const minY = Math.min.apply(null, projected.map(function (point) { return point.y }))
  const maxY = Math.max.apply(null, projected.map(function (point) { return point.y }))

  return {
    minX: minX,
    maxX: maxX,
    minY: minY,
    maxY: maxY,
    width: Math.max(0, maxX - minX),
    height: Math.max(0, maxY - minY),
    area: Math.max(0, maxX - minX) * Math.max(0, maxY - minY),
    minDepth: Math.min.apply(null, projected.map(function (point) { return point.depth })),
    maxDepth: Math.max.apply(null, projected.map(function (point) { return point.depth })),
  }
}

/** Applies the requested Y offset relative to the target's geometric center. */
export function buildTargetPosition(bounds: ViewpointBounds, targetY: number): ViewpointVector3 {
  return {
    x: bounds.center.x,
    y: bounds.center.y + targetY,
    z: bounds.center.z,
  }
}
